import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { BotaoPequeno, Hint } from "../modules/profiles-tracking/components/edit-profile-modal/components/EditorChrome";
import {
  aoMudarConvite,
  ativarNotificacoes,
  desativarNotificacoes,
  enviarTeste,
  estaInstalado,
  instalar,
  podeInstalar,
  situacaoAtual,
  type SituacaoDasNotificacoes,
} from ".";

const Cartao = styled.section`
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 16px 18px;
  border-radius: 16px;
  background: #eaf1fb;
  border: 1px solid #d6e4f7;
  color: #1f1f1f;
  .icone {
    font-size: 28px;
    color: #0b4fa8;
  }
  h3 {
    margin: 0 0 4px;
    font-size: 15px;
    font-weight: 600;
  }
  ol {
    margin: 6px 0 0;
    padding-left: 18px;
    font-size: 13px;
    line-height: 20px;
    color: #3c4043;
  }
`;

/**
 * Home do cliente: instalar o app e ligar as notificações, com o passo a passo
 * do iPhone (lá a notificação só funciona com o app na Tela de Início).
 */
export const CartaoDoApp: React.FC = () => {
  const [situacao, setSituacao] = useState<SituacaoDasNotificacoes | null>(null);
  const [instalavel, setInstalavel] = useState(podeInstalar());
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    situacaoAtual().then(setSituacao).catch(() => setSituacao("sem-suporte"));
    return aoMudarConvite(() => setInstalavel(podeInstalar())) as () => void;
  }, []);

  if (!situacao) return null;

  const ativar = async () => {
    setOcupado(true);
    try {
      const nova = await ativarNotificacoes();
      setSituacao(nova);
      if (nova === "ativadas") {
        toast.success("Pronto! Você vai receber aqui o que estamos fazendo pelo seu perfil.");
        await enviarTeste().catch(() => 0);
      }
    } catch (erro) {
      toast.error(erro instanceof Error ? erro.message : "Não foi possível ativar as notificações.");
    } finally {
      setOcupado(false);
    }
  };

  const desativar = async () => {
    setOcupado(true);
    await desativarNotificacoes().catch(() => undefined);
    setSituacao("desativadas");
    setOcupado(false);
  };

  const testar = async () => {
    setOcupado(true);
    try {
      const enviados = await enviarTeste();
      toast.info(enviados > 0 ? "Notificação de teste enviada." : "Nenhum aparelho recebeu. Desative e ative de novo.");
    } catch {
      toast.error("Não foi possível enviar o teste.");
    } finally {
      setOcupado(false);
    }
  };

  const instalarApp = async () => {
    if (await instalar()) toast.success("App instalado. Abra o GeoRanking pela tela inicial do celular.");
  };

  if (situacao === "ativadas") {
    return (
      <Cartao aria-live="polite">
        <span className="material-icons icone" aria-hidden="true">notifications_active</span>
        <div style={{ flex: 1 }}>
          <h3>Notificações ativadas neste aparelho</h3>
          <Hint>Todo fim de dia você recebe o que fizemos no seu perfil, e a equipe pode te avisar das novidades.</Hint>
          <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <BotaoPequeno type="button" $variante="outlined" onClick={testar} disabled={ocupado}>Enviar teste</BotaoPequeno>
            <BotaoPequeno type="button" $variante="text" onClick={desativar} disabled={ocupado}>Desativar</BotaoPequeno>
          </div>
        </div>
      </Cartao>
    );
  }

  return (
    <Cartao aria-live="polite">
      <span className="material-icons icone" aria-hidden="true">install_mobile</span>
      <div style={{ flex: 1 }}>
        <h3>Tenha o GeoRanking no celular</h3>
        <Hint>Receba todo dia o que a nossa equipe fez pelo seu perfil no Google: postagens, respostas e posições.</Hint>

        {situacao === "iphone-instalar" && (
          <ol>
            <li>Abra esta página no <b>Safari</b>.</li>
            <li>Toque em <b>Compartilhar</b> (o quadrado com a seta para cima).</li>
            <li>Escolha <b>Adicionar à Tela de Início</b> e confirme.</li>
            <li>Abra o GeoRanking pelo ícone novo, entre e toque em <b>Ativar notificações</b>.</li>
          </ol>
        )}
        {situacao === "bloqueadas" && (
          <Hint style={{ display: "block", marginTop: 6, color: "#B3261E" }}>
            As notificações estão bloqueadas neste aparelho. Libere nas configurações do navegador ou do celular
            (Notificações → GeoRanking) e volte aqui.
          </Hint>
        )}
        {situacao === "sem-suporte" && (
          <Hint style={{ display: "block", marginTop: 6 }}>
            Este navegador não recebe notificações. No Android use o Chrome; no iPhone, o Safari.
          </Hint>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {situacao === "desativadas" && (
            <BotaoPequeno type="button" $variante="filled" onClick={ativar} disabled={ocupado}>
              <span className="material-icons" aria-hidden="true">notifications</span>
              {ocupado ? "Ativando..." : "Ativar notificações"}
            </BotaoPequeno>
          )}
          {instalavel && !estaInstalado() && (
            <BotaoPequeno type="button" $variante="outlined" onClick={instalarApp}>
              <span className="material-icons" aria-hidden="true">download</span>
              Instalar o app
            </BotaoPequeno>
          )}
        </div>
      </div>
    </Cartao>
  );
};
